<template>
    <div class="input-wrapper">
        <label v-if="label" :for="inputId" class="input-label">
            {{ label }}
            <span v-if="required" class="input-required">*</span>
        </label>
        <div class="input-container">
            <slot name="prefix" />
            <input
                :id="inputId"
                ref="inputRef"
                v-model="modelValue"
                :type="type"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
                :maxlength="maxlength"
                :class="['input-field', { 'input-error': error }]"
                @blur="handleBlur"
                @focus="emit('focus', $event)"
            />
            <slot name="suffix" />
        </div>
        <p v-if="error" class="input-hint input-hint-error">{{ error }}</p>
        <p v-else-if="hint" class="input-hint">{{ hint }}</p>
    </div>
</template>

<script setup lang="ts">
    import { ref, useId, inject } from 'vue'
    import { FORM_CONTEXT_KEY } from '../form/form.types'

    defineOptions({ name: 's-input' })

    interface Props {
        type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url'
        label?: string
        placeholder?: string
        disabled?: boolean
        readonly?: boolean
        required?: boolean
        error?: string
        hint?: string
        maxlength?: number | string
        /** 字段名，用于表单校验 */
        prop?: string
    }

    const props = withDefaults(defineProps<Props>(), {
        type: 'text',
        disabled: false,
        readonly: false,
        required: false,
    })

    const emit = defineEmits<{
        blur: [event: FocusEvent]
        focus: [event: FocusEvent]
    }>()

    const modelValue = defineModel<string | number>()
    const inputRef = ref<HTMLInputElement>()
    const inputId = useId()

    // 注入表单上下文
    const formContext = inject(FORM_CONTEXT_KEY, null)

    /**
     * 处理失焦事件
     */
    function handleBlur(event: FocusEvent) {
        emit('blur', event)

        // 触发表单校验（blur 触发）
        if (props.prop && formContext) {
            formContext.validateField(props.prop)
        }
    }

    defineExpose({
        focus: () => inputRef.value?.focus(),
        blur: () => inputRef.value?.blur(),
    })
</script>

<style scoped lang="scss">
    // 颜色变量
    $input-bg: rgba(0, 0, 0, 0.05);
    $gray-50: #f9fafb;
    $gray-400: #9ca3af;
    $gray-500: #6b7280;
    $gray-700: #374151;
    $red-500: #ef4444;

    .input-wrapper {
        width: 100%;
    }

    .input-label {
        display: block;
        margin-bottom: 0.375rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: $gray-700;
    }

    .input-required {
        color: $red-500;
    }

    .input-container {
        position: relative;
        display: flex;
        align-items: center;
    }

    .input-field {
        box-sizing: border-box;
        width: 100%;
        height: 48px;
        padding: 0 16px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        background-color: $input-bg;
        transition: all 0.15s ease;

        &::placeholder {
            color: $gray-400;
        }

        &:focus {
            outline: none;
            background-color: rgba(0, 0, 0, 0.08);
        }

        &:disabled {
            color: $gray-500;
            background-color: $gray-50;
            cursor: not-allowed;
        }

        &.input-error {
            background-color: rgba(239, 68, 68, 0.1);
        }
    }

    .input-hint {
        margin-top: 0.25rem;
        font-size: 0.875rem;
        color: $gray-500;

        &.input-hint-error {
            color: $red-500;
        }
    }
</style>
